import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_TITLE,
  OG_LOCALE,
  SITE_NAME,
  SITE_URL,
  SUPPORTED_LANGS,
  TWITTER_HANDLE,
  absoluteUrl,
  type SeoLang,
} from './config';

export type PageSeoInput = {
  title?: string;
  description?: string;
  path?: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article' | 'product' | 'profile';
  noindex?: boolean;
  lang?: SeoLang;
  /** Si false, n'émet pas les hreflang (pages noindex). Défaut true. */
  hreflang?: boolean;
};

const META_ATTR = 'data-bl-seo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const nodes = [...document.head.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${key}"]`)];
  const el = nodes[0] ?? document.createElement('meta');
  if (!nodes[0]) {
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(META_ATTR, '1');
  el.setAttribute('content', content);
  for (const extra of nodes.slice(1)) extra.remove();
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((n) => n.remove());
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  const nodes = [...document.head.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`)];
  const matches = (el: HTMLLinkElement) => {
    if (!extra) return rel !== 'alternate' || !el.hasAttribute('hreflang');
    return Object.entries(extra).every(([k, v]) => el.getAttribute(k) === v);
  };
  const found = nodes.find(matches);
  const el = found ?? document.createElement('link');
  if (!found) {
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  if (extra) {
    for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
  }
  el.setAttribute(META_ATTR, '1');
  el.setAttribute('href', href);
  if (rel === 'canonical') {
    for (const extraNode of nodes) {
      if (extraNode !== el) extraNode.remove();
    }
  }
}

function removeHreflangAlternates() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
}

const HTML_LANG: Record<SeoLang, string> = {
  fr: 'fr-CH',
  de: 'de-CH',
  en: 'en-CH',
  it: 'it-CH',
};

function isDefaultOgImage(image: string): boolean {
  return image === DEFAULT_OG_IMAGE || image.endsWith('/og-default.png');
}

/** Applique title, description, OG, Twitter, canonical, robots, hreflang. */
export function applyPageSeo(input: PageSeoInput = {}) {
  const title = input.title?.trim() || DEFAULT_TITLE;
  const description = input.description?.trim() || DEFAULT_DESCRIPTION;
  const canonical = input.canonical || absoluteUrl(input.path || '/');
  const image = input.image || DEFAULT_OG_IMAGE;
  const imageAlt = input.imageAlt || DEFAULT_OG_IMAGE_ALT;
  const type = input.type || 'website';
  const lang: SeoLang = input.lang || 'fr';
  const robots = input.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = title;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'googlebot', robots);
  upsertMeta('name', 'author', SITE_NAME);
  upsertMeta('name', 'theme-color', '#FC6720');

  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:alt', imageAlt);
  if (isDefaultOgImage(image)) {
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
  } else {
    removeMeta('property', 'og:image:width');
    removeMeta('property', 'og:image:height');
  }
  upsertMeta('property', 'og:locale', OG_LOCALE[lang]);
  removeMeta('property', 'og:locale:alternate');
  for (const l of SUPPORTED_LANGS) {
    if (l === lang) continue;
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:locale:alternate');
    el.setAttribute('content', OG_LOCALE[l]);
    el.setAttribute(META_ATTR, '1');
    document.head.appendChild(el);
  }

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:site', TWITTER_HANDLE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
  upsertMeta('name', 'twitter:image:alt', imageAlt);

  upsertLink('canonical', canonical);

  removeHreflangAlternates();
  if (input.hreflang !== false && !input.noindex) {
    // Une seule URL par page (langue = i18n client). Plusieurs hreflang identiques
    // sont ignorés par Google — on n’émet que x-default.
    upsertLink('alternate', canonical, { hreflang: 'x-default' });
  }
}

export function setHtmlLang(lang: string) {
  const code = lang.slice(0, 2).toLowerCase();
  const normalized = (SUPPORTED_LANGS as readonly string[]).includes(code) ? (code as SeoLang) : 'fr';
  document.documentElement.lang = HTML_LANG[normalized];
}

export { SITE_URL, SITE_NAME, DEFAULT_TITLE, DEFAULT_DESCRIPTION };
